import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import StaffLayout from "@/pages/staff/StaffLayout";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { StatusBadge, TypeBadge } from "@/components/StatusBadge";
import { toast } from "sonner";
import { AlertTriangle, UserPlus, ArrowRight, Pencil, Eye } from "lucide-react";
import EditAppointmentDialog from "@/components/EditAppointmentDialog";
import { useIsGracePeriod } from "@/components/GracePeriodBanner";

export default function StaffDashboard() {
  const isReadOnly = useIsGracePeriod();
  const [appointments, setAppointments] = useState([]);
  const [recentPatients, setRecentPatients] = useState([]);
  const [subInfo, setSubInfo] = useState(null);
  const [editingAppointment, setEditingAppointment] = useState(null);
  const navigate = useNavigate();

  const load = useCallback(() => {
    const today = new Date().toISOString().slice(0, 10);
    // FIFO order — earliest scheduled slot first, so front-desk staff work through
    // today's appointments in the order patients are expected to arrive.
    api.get("/appointments", { params: { date: today, sort: "schedule_asc" } }).then(({ data }) => setAppointments(data.appointments)).catch(() => {});
    api.get("/patients", { params: { activity_date: today, sort_by: "created_at", sort_dir: "desc", page_size: 20 } }).then(({ data }) => setRecentPatients(data.patients)).catch(() => {});
  }, []);

  useEffect(() => {
    load();
    const interval = setInterval(load, 10000);
    return () => clearInterval(interval);
  }, [load]);

  const checkIn = async (a) => {
    try {
      await api.put(`/appointments/${a.id}/status`, { status: "WAITING" });
      toast.success(`${a.patient_name} checked in`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <StaffLayout>
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-slate-900" data-testid="staff-dashboard-heading">Front Desk</h1>
        <Button
          data-testid="staff-new-appointment-btn"
          className="gap-2 bg-teal-700 hover:bg-teal-800"
          disabled={isReadOnly}
          title={isReadOnly ? "Disabled during subscription grace period" : undefined}
          onClick={() => navigate("/staff/appointments/new")}
        >
          <UserPlus className="h-4 w-4" /> New Appointment
        </Button>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="card-surface card-surface-hover rounded-xl border border-slate-200 bg-white shadow-sm p-6">
          <h2 className="font-semibold text-slate-900">Today's Appointments</h2>
          <div className="mt-4 space-y-3">
            {appointments.map((a) => (
              <div
                key={a.id}
                onClick={() => navigate(`/staff/patients/${a.patient_id}`)}
                className="flex cursor-pointer items-center justify-between rounded-lg border border-slate-200 p-3 transition-colors hover:bg-slate-50"
                data-testid={`staff-appt-${a.appointment_code}`}
              >
                <div>
                  <p className="font-medium text-slate-900">{a.patient_name}</p>
                  <p className="text-xs text-slate-500 font-mono">{a.appointment_code} · {a.appointment_time}</p>
                </div>
                <div className="flex items-center gap-2">
                  <TypeBadge type={a.appointment_type} />
                  <StatusBadge status={a.status} />
                  {(a.status === "SCHEDULED" || a.status === "WAITING") && (
                    <button
                      data-testid={`edit-appt-${a.appointment_code}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        setEditingAppointment(a);
                      }}
                      className="rounded-md p-1.5 text-slate-400 hover:bg-slate-100 hover:text-teal-700"
                      title="Edit appointment"
                    >
                      <Pencil className="h-4 w-4" />
                    </button>
                  )}
                  <button
                    data-testid={`view-appt-${a.appointment_code}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/staff/patients/${a.patient_id}`);
                    }}
                    className="rounded-md p-1.5 text-slate-400 hover:bg-slate-100 hover:text-teal-700"
                    title="View patient"
                  >
                    <Eye className="h-4 w-4" />
                  </button>
                  {a.status === "SCHEDULED" && (
                    <Button
                      size="sm"
                      data-testid={`check-in-${a.appointment_code}`}
                      className="bg-teal-700 hover:bg-teal-800"
                      disabled={isReadOnly}
                      title={isReadOnly ? "Disabled during subscription grace period" : undefined}
                      onClick={(e) => {
                        e.stopPropagation();
                        checkIn(a);
                      }}
                    >
                      Check-In
                    </Button>
                  )}
                </div>
              </div>
            ))}
            {appointments.length === 0 && <p className="text-sm text-slate-400">No appointments today.</p>}
          </div>
        </div>

        <div className="card-surface card-surface-hover rounded-xl border border-slate-200 bg-white shadow-sm p-6">
          <div className="flex items-center justify-between">
            <h2 className="font-semibold text-slate-900">Today's Patients</h2>
            <button data-testid="see-all-patients-btn" className="text-xs text-teal-700 hover:underline flex items-center gap-1" onClick={() => navigate("/staff/search")}>
              see all <ArrowRight className="h-3 w-3" />
            </button>
          </div>
          <p className="text-xs text-slate-400">Patients Registered/Visited Today</p>
          <div className="mt-4 space-y-2">
            {recentPatients.map((p) => (
              <button
                key={p.id}
                data-testid={`recent-patient-${p.patient_code}`}
                onClick={() => navigate(`/staff/patients/${p.id}`)}
                className="flex w-full items-center justify-between rounded-lg border border-slate-200 p-3 text-left hover:bg-slate-50"
              >
                <div>
                  <p className="text-sm font-medium text-slate-900">{p.full_name}</p>
                  <p className="text-xs font-mono text-slate-500">{p.patient_code}</p>
                </div>
                <span className="text-xs text-slate-400">{p.mobile}</span>
              </button>
            ))}
            {recentPatients.length === 0 && <p className="text-sm text-slate-400">No patients registered or visited today.</p>}
          </div>
        </div>
      </div>

      <EditAppointmentDialog appointment={editingAppointment} onOpenChange={(open) => !open && setEditingAppointment(null)} onSaved={load} />
    </StaffLayout>
  );
}
